/** Small shared pieces of the objective system: geometry, rule lookup, and the two verdicts. */

import type { Ctx } from '../ctx';
import { collidersFor } from '../props';
import type { Captive, MissionDef, ObjectiveRule, PropDef, PropKind, Rect, World } from '../types';

export function inRect(zone: Rect, x: number, z: number): boolean {
  return x >= zone.x0 && x <= zone.x1 && z >= zone.z0 && z <= zone.z1;
}

export function dist(ax: number, az: number, bx: number, bz: number): number {
  return Math.sqrt((ax - bx) * (ax - bx) + (az - bz) * (az - bz));
}

export function propDef(def: MissionDef, id: number): PropDef | undefined {
  return def.props.find((p) => p.id === id);
}

export function propDestroyed(w: World, id: number): boolean {
  return w.props.find((p) => p.id === id)?.destroyed === true;
}

/** The objective's target props, in the order the mission lists them. */
export function targetProps(def: MissionDef): PropDef[] {
  const out: PropDef[] = [];
  for (const id of def.objective.targetPropIds) {
    const prop = propDef(def, id);
    if (prop) out.push(prop);
  }
  return out;
}

/** Distance from a point to a prop's footprint (0 inside it); to its centre when it has none. */
export function propDistance(prop: PropDef, x: number, z: number): number {
  let best = Infinity;
  for (const c of collidersFor(prop)) {
    let d: number;
    if (c.shape === 'circle') d = Math.max(0, dist(x, z, c.x, c.z) - c.r);
    else {
      const nx = Math.min(c.x + c.hx, Math.max(c.x - c.hx, x));
      const nz = Math.min(c.z + c.hz, Math.max(c.z - c.hz, z));
      d = dist(x, z, nx, nz);
    }
    if (d < best) best = d;
  }
  return best === Infinity ? dist(x, z, prop.x, prop.z) : best;
}

/** The nearest prop of one of `kinds` to a point, by footprint, with its distance. */
export function nearestProp(
  def: MissionDef,
  kinds: ReadonlyArray<PropKind>,
  x: number,
  z: number,
): { prop: PropDef; distance: number } | null {
  let best: { prop: PropDef; distance: number } | null = null;
  for (const prop of def.props) {
    if (!kinds.includes(prop.kind)) continue;
    const distance = propDistance(prop, x, z);
    if (!best || distance < best.distance) best = { prop, distance };
  }
  return best;
}

/** Taken at gunpoint, hands up, going only where his captor goes: the officer of mission 4, the scientist of 30. */
export function isPrisoner(kind: Captive['kind']): boolean {
  return kind === 'officer' || kind === 'scientist';
}

/** How many of `total` things the mission needs done, given its `minFraction` (at least one). */
export function neededCount(def: MissionDef, total: number): number {
  return Math.max(1, Math.ceil(total * def.objective.minFraction));
}

/** What the failure reasons call the man this mission is about. */
export function targetName(def: MissionDef, fallback: string): string {
  return def.objective.targetName ?? fallback;
}

export function findRule<T extends ObjectiveRule['type']>(
  def: MissionDef,
  type: T,
): Extract<ObjectiveRule, { type: T }> | undefined {
  return def.objective.rules.find((r): r is Extract<ObjectiveRule, { type: T }> => r.type === type);
}

/** The objective is lost. The mission goes on: the ranger may still extract, and it scores as a failure. */
export function failObjective(ctx: Ctx, reason: string): void {
  const o = ctx.world.objective;
  if (o.failed) return;
  o.failed = true;
  o.failReason = reason;
  ctx.events.push({ type: 'objectiveFailed', reason });
}

export function completeObjective(ctx: Ctx): void {
  const o = ctx.world.objective;
  if (o.complete || o.failed) return;
  o.complete = true;
  ctx.events.push({ type: 'objectiveComplete' });
}

/** Killed, trucked away, bolted: either way he is not coming home with us. */
export function loseCaptive(ctx: Ctx, c: Captive): void {
  if (c.state === 'lost' || c.state === 'boarded') return;
  c.state = 'lost';
  c.moving = false;
  c.climbTicks = 0;
  c.path = [];
  ctx.events.push({ type: 'captive', id: c.id, what: 'lost' });
}

/** Let the held captives out: they fall in behind the ranger, and whoever was waiting for this wakes. */
export function openCells(ctx: Ctx, only?: (c: Captive) => boolean): void {
  const o = ctx.world.objective;
  for (const c of o.captives) {
    if (c.state !== 'held' || (only && !only(c))) continue;
    c.state = 'following';
    ctx.events.push({ type: 'captive', id: c.id, what: 'freed' });
    o.opened = true;
  }
}
